// "Pairs well with" rail in the cart drawer.
// Loads Shopify's related-product recommendations for the newest cart item.
//
// Speed:
// - Results are cached for the browsing session (sessionStorage, 30 min), so reopening
//   the drawer on any page is instant.
// - Smart prefetch: on a product page, recommendations are fetched only once the shopper
//   shows buying intent (picks a color/size, or hovers/touches/focuses Add to Cart).
//   Normal browsing triggers no extra requests.
// - While a request is still in flight, a light placeholder holds the space so the
//   drawer doesn't jump when the cards arrive.
// Prefetched HTML is parsed inertly, so no shirt images download until the rail is shown.
//
// Failure handling: any error, bad response, or a request slower than 6 seconds hides the
// rail quietly. Failures are never cached, so the next drawer open simply tries again.
// The rest of the drawer (items, totals, checkout) never depends on this file.
if (!customElements.get('cc-cart-recs')) {
  const TTL = 30 * 60 * 1000;
  const TIMEOUT = 6000;
  const STORE_PREFIX = 'ccRecs:';
  const memory = new Map(); // productId -> unfiltered cards HTML
  const inflight = new Map(); // productId -> Promise<string|null>

  const defaultUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}recommendations/products`;
  };

  const readStore = (id) => {
    try {
      const raw = window.sessionStorage.getItem(STORE_PREFIX + id);
      if (!raw) return undefined;
      const saved = JSON.parse(raw);
      if (!saved || typeof saved.h !== 'string' || Date.now() - saved.t > TTL) return undefined;
      return saved.h;
    } catch (error) {
      return undefined;
    }
  };

  const writeStore = (id, html) => {
    try {
      window.sessionStorage.setItem(STORE_PREFIX + id, JSON.stringify({ t: Date.now(), h: html }));
    } catch (error) {}
  };

  const cached = (id) => {
    if (memory.has(id)) return memory.get(id);
    const html = readStore(id);
    if (html !== undefined) memory.set(id, html);
    return html;
  };

  const fetchRecs = (rawId, baseUrl) => {
    const id = String(rawId);
    const hit = cached(id);
    if (hit !== undefined) return Promise.resolve(hit);
    if (inflight.has(id)) return inflight.get(id);

    const url = `${baseUrl || defaultUrl()}?section_id=cc-cart-recommendations&product_id=${encodeURIComponent(id)}&limit=10&intent=related`;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timer;
    const timeout = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        if (controller) controller.abort();
        reject(new Error('timeout'));
      }, TIMEOUT);
    });
    const request = Promise.race([
      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error('recs'))))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          return [...doc.querySelectorAll('.cc-rec')].map((card) => card.outerHTML).join('');
        }),
      timeout,
    ])
      .then((html) => {
        memory.set(id, html);
        writeStore(id, html);
        return html;
      })
      .catch(() => null)
      .finally(() => {
        clearTimeout(timer);
        inflight.delete(id);
      });
    inflight.set(id, request);
    return request;
  };

  const filterCards = (html, exclude) => {
    const inCart = new Set((exclude || '').split(',').filter(Boolean));
    const template = document.createElement('template');
    template.innerHTML = html;
    return [...template.content.querySelectorAll('.cc-rec')]
      .filter((card) => !inCart.has(card.dataset.productId))
      .slice(0, 8)
      .map((card) => card.outerHTML)
      .join('');
  };

  const SKELETON =
    '<span class="cc-rec cc-rec--skeleton" aria-hidden="true"><span class="cc-rec__media"></span><span class="cc-rec__bar"></span><span class="cc-rec__bar cc-rec__bar--short"></span></span>'.repeat(
      3
    );

  // Smart prefetch: buying-intent signals on a product page.
  const prefetched = new Set();
  const onIntent = (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    const trigger = target.closest('product-form button[name="add"], variant-selects');
    if (!trigger) return;
    const info = trigger.closest('product-info[data-product-id]');
    const id = info && info.dataset.productId;
    if (!id || prefetched.has(id)) return;
    prefetched.add(id);
    fetchRecs(id);
  };
  ['pointerover', 'pointerdown', 'touchstart', 'focusin', 'change'].forEach((type) =>
    document.addEventListener(type, onIntent, { passive: true, capture: true })
  );

  window.ccRecsPrefetch = fetchRecs;

  customElements.define(
    'cc-cart-recs',
    class CcCartRecs extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('.cc-recs__rail');
        this.prev = this.querySelector('[data-dir="-1"]');
        this.next = this.querySelector('[data-dir="1"]');
        this.prev.addEventListener('click', () => this.page(-1));
        this.next.addEventListener('click', () => this.page(1));
        this.rail.addEventListener('scroll', () => this.updateArrows(), { passive: true });

        // Already fetched this session (prefetch or earlier open): show instantly, no request.
        if (this.renderFromCache()) return;

        this.drawer = this.closest('cart-drawer');
        if (!this.drawer || this.drawer.classList.contains('active')) {
          this.load();
          return;
        }
        this.observer = new MutationObserver(() => {
          if (!this.drawer.classList.contains('active')) return;
          this.observer.disconnect();
          this.load();
        });
        this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      renderFromCache() {
        const id = this.dataset.productId;
        if (!id) return false;
        const html = cached(String(id));
        if (html === undefined) return false;
        this.render(html);
        return true;
      }

      async load() {
        const id = this.dataset.productId;
        if (!id) return;
        if (this.renderFromCache()) return;

        this.rail.innerHTML = SKELETON;
        this.setAttribute('aria-busy', 'true');
        this.hidden = false;

        const html = await fetchRecs(id, this.dataset.url);
        if (!this.isConnected) return;
        if (html === null) {
          this.removeAttribute('aria-busy');
          this.hidden = true;
          return;
        }
        this.render(html);
      }

      render(html) {
        const cards = filterCards(html, this.dataset.exclude);
        this.removeAttribute('aria-busy');
        if (!cards) {
          this.hidden = true;
          return;
        }
        this.rail.innerHTML = cards;
        this.hidden = false;
        this.updateArrows();
      }

      page(direction) {
        this.rail.scrollBy({ left: direction * this.rail.clientWidth * 0.8, behavior: 'smooth' });
      }

      updateArrows() {
        const max = this.rail.scrollWidth - this.rail.clientWidth;
        this.prev.disabled = this.rail.scrollLeft <= 2;
        this.next.disabled = this.rail.scrollLeft >= max - 2;
      }
    }
  );
}
